import React, { useMemo } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { createMarkdownComponents } from './MarkdownComponents';

/**
 * NotebookRenderer
 * Props:
 *  - content: string (raw JSON of a Jupyter .ipynb-like object)
 *  - isDarkMode: boolean
 *  - filename?: string (optional notebook filename for download, default: "notebook.ipynb")
 */
const NotebookRenderer = ({ content, isDarkMode, filename = 'notebook.ipynb' }) => {
  // Parse notebook JSON – hook MUST be before any return
  const [parseError, notebookData] = useMemo(() => {
    try {
      return [null, JSON.parse(content)];
    } catch (e) {
      return [e, null];
    }
  }, [content]);

  const cells = notebookData?.cells || [];

  // Build attachments map (for markdown images like ![](attachment:foo.png))
  const attachmentsMap = useMemo(() => {
    const map = new Map();
    if (!notebookData) return map;

    cells.forEach((cell) => {
      if (cell.attachments && typeof cell.attachments === 'object') {
        for (const [name, blobs] of Object.entries(cell.attachments)) {
          if (blobs['image/png']) {
            map.set(name, `data:image/png;base64,${blobs['image/png']}`);
          } else if (blobs['image/jpeg']) {
            map.set(name, `data:image/jpeg;base64,${blobs['image/jpeg']}`);
          } else if (blobs['image/svg+xml']) {
            // if it's raw SVG text in attachments, we still base64 it
            const svg = blobs['image/svg+xml'];
            const encoded =
              typeof window !== 'undefined'
                ? window.btoa(unescape(encodeURIComponent(svg)))
                : Buffer.from(svg, 'utf8').toString('base64');
            map.set(name, `data:image/svg+xml;base64=${encoded}`);
          }
        }
      }
    });

    return map;
  }, [notebookData, cells]);

  const handleDownload = () => {
    if (typeof window === 'undefined') return; // SSR guard

    try {
      const payload =
        typeof content === 'string' ? content : JSON.stringify(content, null, 2);

      const blob = new Blob([payload], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download notebook:', err);
    }
  };

  if (parseError) {
    return (
      <div className="p-4 bg-status-error-light-bg dark:bg-status-error-dark-bg text-status-error-light-text dark:text-status-error-dark-text rounded-lg">
        Invalid notebook JSON format
      </div>
    );
  }

  // --- Helpers ---------------------------------------------------------------

  // Some nbformat variants store strings as arrays of lines
  const normalizeText = (val) => {
    if (Array.isArray(val)) return val.join('');
    return typeof val === 'string' ? val : '';
  };

  // Base64 helper for SVG strings (used in outputs)
  const svgToDataUri = (svgString) => {
    const encoded =
      typeof window !== 'undefined'
        ? window.btoa(unescape(encodeURIComponent(svgString)))
        : Buffer.from(svgString, 'utf8').toString('base64');

    return `data:image/svg+xml;base64,${encoded}`;
  };

  // JSON pretty-print
  const prettyJSON = (obj) => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  // Replace "attachment:filename" with data URIs in markdown
  const resolveMarkdownAttachments = (md) => {
    if (!md) return '';
    return md.replace(/!\[(.*?)\]\(attachment:([^)]+)\)/g, (match, alt, fname) => {
      const uri = attachmentsMap.get(fname);
      return uri ? `![${alt}](${uri})` : match;
    });
  };

  // --- Output Renderer -------------------------------------------------------

  const renderOutput = (output) => {
    // Streams (stdout/stderr)
    if (output.output_type === 'stream') {
      return (
        <pre className="bg-gray-100 dark:bg-gray-900 p-3 rounded-md overflow-x-auto text-sm">
          <code className="text-gray-800 dark:text-gray-200">
            {normalizeText(output.text)}
          </code>
        </pre>
      );
    }

    // Errors
    if (output.output_type === 'error') {
      return (
        <pre className="bg-red-100 dark:bg-red-900/30 p-3 rounded-md overflow-x-auto text-sm border border-red-300 dark:border-red-700">
          <code className="text-red-800 dark:text-red-200">
            {output.ename}: {output.evalue}
            {output.traceback && '\n' + output.traceback.join('\n')}
          </code>
        </pre>
      );
    }

    // execute_result / display_data
    if (
      output.output_type === 'execute_result' ||
      output.output_type === 'display_data'
    ) {
      const data = output.data || {};

      // Images
      if (data['image/png']) {
        const b64 = normalizeText(data['image/png']);
        return (
          <img
            src={`data:image/png;base64,${b64}`}
            alt="Output"
            className="max-w-full h-auto rounded-lg shadow-md my-2"
          />
        );
      }
      if (data['image/jpeg']) {
        const b64 = normalizeText(data['image/jpeg']);
        return (
          <img
            src={`data:image/jpeg;base64,${b64}`}
            alt="Output"
            className="max-w-full h-auto rounded-lg shadow-md my-2"
          />
        );
      }
      if (data['image/svg+xml']) {
        const svg = normalizeText(data['image/svg+xml']);
        const uri = svgToDataUri(svg);
        return (
          <img
            src={uri}
            alt="Output SVG"
            className="max-w-full h-auto rounded-lg shadow-md my-2"
          />
        );
      }

      // HTML
      if (data['text/html']) {
        const htmlData = normalizeText(data['text/html']);
        return (
          <div
            className="prose dark:prose-invert max-w-none"
            // Trust your own content source; otherwise sanitize before injecting
            dangerouslySetInnerHTML={{ __html: htmlData }}
          />
        );
      }

      // JSON (e.g., application/json)
      if (data['application/json']) {
        const jsonData = data['application/json'];
        return (
          <pre className="bg-gray-100 dark:bg-gray-900 p-3 rounded-md overflow-x-auto text-sm">
            <code className="text-gray-800 dark:text-gray-200">
              {prettyJSON(jsonData)}
            </code>
          </pre>
        );
      }

      // text/plain fallback
      if (data['text/plain']) {
        const textData = normalizeText(data['text/plain']);
        return (
          <pre className="bg-gray-100 dark:bg-gray-900 p-3 rounded-md overflow-x-auto text-sm">
            <code className="text-gray-800 dark:text-gray-200">{textData}</code>
          </pre>
        );
      }
    }

    // Nothing matched
    return null;
  };

  // --- Cell Renderer ---------------------------------------------------------

  const renderCell = (cell, index) => {
    const source = normalizeText(cell.source);

    // Markdown cell
    if (cell.cell_type === 'markdown') {
      const md = resolveMarkdownAttachments(source);
      return (
        <div
          key={`md-${index}`}
          className="mb-4 p-4 bg-ui-light-card dark:bg-ui-dark-card rounded-lg border border-ui-light-border dark:border-ui-dark-border overflow-x-auto"
        >
          <div className="prose dark:prose-invert max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={createMarkdownComponents(isDarkMode)}
            >
              {md}
            </ReactMarkdown>
          </div>
        </div>
      );
    }

    // Code cell
    if (cell.cell_type === 'code') {
      return (
        <div
          key={`code-${index}`}
          className="mb-4 border border-ui-light-border dark:border-ui-dark-border rounded-lg overflow-hidden"
        >
          {/* Execution count */}
          <div className="flex items-center gap-2 bg-ui-light-background dark:bg-ui-dark-background px-4 py-2 border-b border-ui-light-border dark:border-ui-dark-border">
            <span className="text-xs font-mono text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
              In [{cell.execution_count ?? ' '}]:
            </span>
          </div>

          {/* Code input */}
          <div className="relative">
            <SyntaxHighlighter
              language="python"
              style={isDarkMode ? vscDarkPlus : prism}
              customStyle={{
                margin: 0,
                borderRadius: 0,
                fontSize: '0.875rem',
                padding: '1rem',
              }}
            >
              {source}
            </SyntaxHighlighter>
          </div>

          {/* Outputs */}
          {Array.isArray(cell.outputs) && cell.outputs.length > 0 && (
            <div className="bg-white dark:bg-gray-950 border-t border-ui-light-border dark:border-ui-dark-border">
              <div className="px-4 py-2">
                <span className="text-xs font-mono text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                  Out [{cell.execution_count ?? ' '}]:
                </span>
              </div>
              <div className="px-4 pb-4 space-y-2">
                {cell.outputs.map((output, outIndex) => (
                  <div key={outIndex}>{renderOutput(output)}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    // Raw cell
    if (cell.cell_type === 'raw') {
      return (
        <pre
          key={`raw-${index}`}
          className="mb-4 p-3 bg-gray-50 dark:bg-gray-900 border border-ui-light-border dark:border-ui-dark-border rounded-lg overflow-x-auto text-sm"
        >
          {source}
        </pre>
      );
    }

    return null;
  };

  // --- Render ----------------------------------------------------------------

  return (
    <div className="my-4 space-y-2">
      {/* Notebook header */}
      <div className="flex items-center justify-between mb-4 p-3 bg-primary-50 dark:bg-primary-900/10 rounded-lg border border-primary-200 dark:border-primary-800">
        <div className="flex items-center gap-2">
          <svg
            className="w-5 h-5 text-primary-600 dark:text-primary-400"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M9 4v16h2V4H9zm4 0v16h2V4h-2z" />
          </svg>
          <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
            Jupyter Notebook ({cells.length} {cells.length === 1 ? 'cell' : 'cells'})
          </span>
        </div>

        <button
          type="button"
          onClick={handleDownload}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md
                     text-primary-700 dark:text-primary-300
                     hover:bg-primary-100 dark:hover:bg-primary-900/30
                     border border-primary-200 dark:border-primary-700"
          title="Download notebook"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3M8 11l4 4 4-4M12 4v11"
            />
          </svg>
          <span>.ipynb</span>
        </button>
      </div>

      {/* Cells */}
      {cells.map((cell, index) => renderCell(cell, index))}
    </div>
  );
};

export default NotebookRenderer;